import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  FaStar,
  FaCheckCircle,
  FaTimesCircle,
  FaShieldAlt,
  FaClock,
  FaGift,
  FaArrowLeft,
} from 'react-icons/fa';

const OfferDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const offerData = useMemo(() => {
    const offers: Record<string, any> = {
      circusbet: {
        name: 'CircusBet',
        bonus: '100€ de bonus',
        rating: 4.8,
        image: '/images/offers/circusbet.webp',
        minDeposit: '10€',
        wagering: '35x',
        maxBet: '5€',
        withdrawal: '24-48 heures',
        license: 'ARJEL',
        url: 'https://www.circusbet.fr/',
        features: [
          'Retraits rapides',
          'Paris en direct',
          'Application mobile',
          'Support 24/7',
          'Programme VIP',
        ],
        pros: [
          'Excellent service client',
          'Large sélection de sports',
          'Retraits rapides',
          'Plateforme mobile optimisée',
        ],
        cons: [
          'Options de cryptomonnaies limitées',
          'Certaines restrictions géographiques',
        ],
        description: `CircusBet se distingue comme l'un des bookmakers français les plus fiables, offrant
        aux parieurs une expérience exceptionnelle avec des bonus généreux et un service client de premier plan.
        Avec une licence ARJEL, vous pouvez parier en toute confiance, sachant que vos fonds et vos informations
        personnelles sont protégés par des normes réglementaires strictes.`,
      },
      genybet: {
        name: 'GenyBet',
        bonus: '200€ de bonus',
        rating: 4.9,
        image: '/images/offers/genybet.webp',
        minDeposit: '20€',
        wagering: '30x',
        maxBet: '5€',
        withdrawal: '12-24 heures',
        license: 'ARJEL',
        url: 'https://www.genybet.fr/',
        features: [
          'Programme VIP',
          'Support 24/7',
          'Cotes exclusives',
          'Cashback',
          'Accès aux tournois',
        ],
        pros: [
          'Expérience VIP premium',
          'Cotes compétitives',
          'Retraits très rapides',
          'Conditions de bonus généreuses',
        ],
        cons: [
          'Dépôt minimum plus élevé',
          'Méthodes de paiement limitées',
        ],
        description: `GenyBet propose une expérience de paris premium conçue pour les parieurs sérieux qui
        apprécient la qualité et l'exclusivité. La plateforme combine une technologie de pointe avec une élégance
        traditionnelle, créant un environnement où chaque pari est spécial. Leur programme VIP est parmi les plus
        récompensants de l'industrie.`,
      },
      netbet: {
        name: 'NetBet',
        bonus: '150€ de bonus',
        rating: 4.7,
        image: '/images/offers/netbet.webp',
        minDeposit: '10€',
        wagering: '40x',
        maxBet: '5€',
        withdrawal: '24-72 heures',
        license: 'ARJEL',
        url: 'https://www.netbet.fr/',
        features: [
          'Paris sportifs',
          'Cashback',
          'Tournois',
          'Streaming en direct',
          'Optimisé mobile',
        ],
        pros: [
          'Sports et casino combinés',
          'Tournois réguliers',
          'Bonne expérience mobile',
          'Plusieurs options de paiement',
        ],
        cons: [
          'Conditions de mise plus élevées',
          'Retraits parfois plus lents',
        ],
        description: `NetBet offre une plateforme de paris complète qui combine parfaitement les paris sportifs
        avec les jeux de casino. Que vous placiez un pari sur votre équipe favorite ou que vous jouiez au casino,
        NetBet offre une expérience unifiée qui garde tout au même endroit. Leur calendrier de tournois est
        particulièrement impressionnant, avec des compétitions régulières offrant des prix substantiels.`,
      },
    };

    return offers[id || ''] || offers.circusbet;
  }, [id]);

  // Generate chart data (simplified - in real app, use a charting library)
  const chartData = [
    { label: 'Valeur du bonus', value: 85 },
    { label: 'Variété de sports', value: 90 },
    { label: 'Vitesse de retrait', value: 88 },
    { label: 'Support client', value: 92 },
    { label: 'Expérience mobile', value: 87 },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Review',
    itemReviewed: {
      '@type': 'Organization',
      name: offerData.name,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: offerData.rating,
      bestRating: 5,
    },
    author: {
      '@type': 'Organization',
      name: 'PariSport France',
    },
  };

  return (
    <>
      <SEO
        title={`Avis ${offerData.name} - Bookmaker Français`}
        description={`Avis complet du bookmaker ${offerData.name}. Bonus de bienvenue ${offerData.bonus}, cotes, sécurité et analyse d'experts.`}
        type="article"
        url={`/offers/${id}`}
        image={`https://parisportfrance.com${offerData.image}`}
        schema={schema}
      />

      <div className="pt-24 md:pt-32 pb-20 min-h-screen bg-dark-bg">
        <div className="container mx-auto px-4">
          <Link
            to="/offers"
            className="inline-flex items-center space-x-2 text-gray-400 hover:text-yellow-400 mb-6 transition-colors"
          >
            <FaArrowLeft />
            <span>Retour aux bookmakers</span>
          </Link>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Header */}
              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-6">
                  <img
                    src={offerData.image}
                    alt={offerData.name}
                    className="w-32 h-32 object-contain rounded-lg"
                    loading="eager"
                  />
                  <div>
                    <h1 className="text-4xl font-bold text-white mb-2">{offerData.name}</h1>
                    <div className="flex items-center space-x-4 mb-3">
                      <div className="flex items-center space-x-1">
                        <FaStar className="text-yellow-400" />
                        <span className="text-2xl font-bold text-white">{offerData.rating}</span>
                        <span className="text-gray-400">/5</span>
                      </div>
                      <div className="text-yellow-400 font-semibold text-xl">
                        {offerData.bonus}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 text-green-400">
                      <FaShieldAlt />
                      <span className="text-sm">{offerData.license}</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-300 text-lg leading-relaxed">{offerData.description}</p>
              </div>

              {/* Performance Chart */}
              <div className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Évaluations de Performance</h2>
                <div className="space-y-4">
                  {chartData.map((item, index) => (
                    <div key={index}>
                      <div className="flex justify-between mb-2">
                        <span className="text-gray-300">{item.label}</span>
                        <span className="text-yellow-400 font-semibold">{item.value}%</span>
                      </div>
                      <div className="w-full bg-dark-surface rounded-full h-3">
                        <div
                          className="bg-gradient-to-r from-yellow-400 to-orange-500 h-3 rounded-full transition-all"
                          style={{ width: `${item.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pros & Cons */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <FaCheckCircle className="text-green-400 mr-2" />
                    Avantages
                  </h3>
                  <ul className="space-y-2">
                    {offerData.pros.map((pro: string, index: number) => (
                      <li key={index} className="text-gray-300 flex items-start">
                        <span className="text-green-400 mr-2">✓</span>
                        {pro}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <FaTimesCircle className="text-red-400 mr-2" />
                    Inconvénients
                  </h3>
                  <ul className="space-y-2">
                    {offerData.cons.map((con: string, index: number) => (
                      <li key={index} className="text-gray-300 flex items-start">
                        <span className="text-red-400 mr-2">✗</span>
                        {con}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Bonus Details */}
              <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                  <FaGift className="text-yellow-400 mr-2" />
                  Détails du Bonus
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Bonus:</span>
                    <span className="text-white font-semibold">{offerData.bonus}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Dépôt min:</span>
                    <span className="text-white">{offerData.minDeposit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Conditions de mise:</span>
                    <span className="text-white">{offerData.wagering}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Mise max:</span>
                    <span className="text-white">{offerData.maxBet}</span>
                  </div>
                </div>
                {offerData.url ? (
                  <a
                    href={offerData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full mt-6 px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all text-center"
                  >
                    Réclamer le bonus
                  </a>
                ) : (
                  <button className="w-full mt-6 px-6 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-dark-bg rounded-lg font-semibold transition-all">
                    Réclamer le bonus
                  </button>
                )}
              </div>

              {/* Key Info */}
              <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Informations Clés</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <FaClock className="text-yellow-400" />
                    <span className="text-gray-300">
                      Retrait: <span className="text-white">{offerData.withdrawal}</span>
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <FaShieldAlt className="text-yellow-400" />
                    <span className="text-gray-300">
                      Licence: <span className="text-white">{offerData.license}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="bg-dark-card border border-dark-border rounded-xl p-6">
                <h3 className="text-xl font-bold text-white mb-4">Caractéristiques</h3>
                <ul className="space-y-2">
                  {offerData.features.map((feature: string, index: number) => (
                    <li key={index} className="text-gray-300 flex items-center">
                      <FaCheckCircle className="text-green-400 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OfferDetail;

